import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../apiConfig";
import { ChevronLeft, Edit2 } from "lucide-react";
import { BarChart, ScatterChart } from "@mui/x-charts";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import indiaJson from "../../maps/india.json";

import {
  ComposedChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Brush,
} from "recharts";
import "./reportview.css";

echarts.registerMap("india", indiaJson);

// Shared Y-axis zoom helpers (line chart trend + line/stacked column)
const Y_AXIS_TOP_GAP = 10;
const Y_AXIS_TICK_COUNT = 5; // includes both endpoints → 4 equal intervals

/**
 * Round the padded max UP so every Y-axis tick is spaced evenly.
 * Without this, Recharts keeps the raw domain max as the last tick
 * (e.g. 0, 35, 70, 105, 128) instead of completing the step (140).
 */
const getEvenYAxisBounds = (dataMin, dataMax, gap = Y_AXIS_TOP_GAP, tickCount = Y_AXIS_TICK_COUNT) => {
  const rawMin = dataMin;
  const rawMax = dataMax + gap;
  const divisions = Math.max(1, tickCount - 1);
  const range = rawMax - rawMin;
  if (range <= 0) {
    return { yMin: rawMin, yMax: rawMin + divisions, ticks: [rawMin, rawMin + divisions] };
  }

  const roughStep = range / divisions;
  const stepBase = roughStep >= 5 ? 5 : 1;
  const step = Math.max(stepBase, Math.ceil(roughStep / stepBase) * stepBase);

  const yMin = Math.floor(rawMin / step) * step;
  const yMax = yMin + step * divisions;
  const finalMax = yMax < rawMax ? yMin + step * Math.ceil((rawMax - yMin) / step) : yMax;

  const ticks = [];
  for (let t = yMin; t <= finalMax + 1e-9; t += step) {
    ticks.push(Math.round(t));
  }
  return { yMin, yMax: finalMax, ticks };
};

/** Vertical dual-handle slider driving a YAxis `domain`. */
const VerticalZoomSlider = ({ min, max, value, onChange, height = 260 }) => {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(null); // 'low' | 'high' | null

  const toPct = (v) => ((v - min) / (max - min)) * 100;

  const fromClientY = (clientY) => {
    const rect = trackRef.current.getBoundingClientRect();
    const pct = 1 - (clientY - rect.top) / rect.height;
    const clamped = Math.min(1, Math.max(0, pct));
    return Math.round(min + clamped * (max - min));
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    const raw = fromClientY(e.clientY);
    const [low, high] = value;
    if (dragging === "low") {
      onChange([Math.min(raw, high - 1), high]);
    } else {
      onChange([low, Math.max(raw, low + 1)]);
    }
  };

  const handlePointerUp = () => setDragging(null);

  React.useEffect(() => {
    if (!dragging) return;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [dragging, value]);

  const lowPct = toPct(value[0]);
  const highPct = toPct(value[1]);

  return (
    <div style={{ width: 40, height, display: "flex", flexDirection: "column", alignItems: "center", userSelect: "none" }}>
      <div style={{ fontSize: 11, color: "#374151", marginBottom: 4, fontWeight: 500 }}>
        {max}
      </div>

      <div
        ref={trackRef}
        style={{ position: "relative", width: 4, flex: 1, background: "#e5e7eb", borderRadius: 2 }}
      >
        <div
          style={{
            position: "absolute", left: 0, width: 4,
            bottom: `${lowPct}%`, height: `${highPct - lowPct}%`,
            background: "#3b82f6", borderRadius: 2,
          }}
        />
        <div
          onPointerDown={() => setDragging("high")}
          title={value[1]}
          style={{
            position: "absolute", left: -6, bottom: `calc(${highPct}% - 7px)`,
            width: 16, height: 14, borderRadius: 4, background: "#3b82f6",
            cursor: "ns-resize", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
        <div
          onPointerDown={() => setDragging("low")}
          title={value[0]}
          style={{
            position: "absolute", left: -6, bottom: `calc(${lowPct}% - 7px)`,
            width: 16, height: 14, borderRadius: 4, background: "#3b82f6",
            cursor: "ns-resize", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
      </div>

      <div style={{ fontSize: 11, color: "#374151", marginTop: 4, fontWeight: 500 }}>
        {min}
      </div>
    </div>
  );
};

const ReportView = () => {
  const { inputID, reportId, reportCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mart_id = location.state?.mart_id;
  const projectId = location.state?.projectId || location.state?.project_id;
  const clientID = location.state?.clientID;
  const roleId = location.state?.roleId;
  const [data, setData] = useState(null);
  const [visualData, setVisualData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visualLoading, setVisualLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visualError, setVisualError] = useState(null);
  const [reportSubtitle, setReportSubTitle] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [activeTab, setActiveTab] = useState("table");
  const [stackOrder, setStackOrder] = useState("none");

  const [kpiList, setKpiList] = useState([]);
  const [selectedKpiId, setSelectedKpiId] = useState("");
  const isKpiDependent = location.state?.isKpiDependent;

  const availableStackOrder = [
    "none",
    "reverse",
    "appearance",
    "ascending",
    "descending",
  ];

  const transformToMuiLineChart = (apiData) => {
    if (!apiData || !apiData.data || typeof apiData.data !== "object") {
      return { xAxisData: [], seriesData: [] };
    }

    const metricKeys = Object.keys(apiData.data);
    const months = Object.keys(apiData.data[metricKeys[0]] || {});
    const seriesData = metricKeys.map((key) => ({
      id: key,
      label: key,
      data: months.map((month) => {
        const value = apiData.data[key][month];
        return value == null ? null : Math.round(value * 100) / 100;
      }),
    }));

    return {
      xAxisData: months,
      seriesData,
    };
  };

  useEffect(() => {
    const titleFromState = location.state?.title;
    const subtitleFromState = location.state?.subtitle;
    if (titleFromState && subtitleFromState) {
      setReportTitle(titleFromState);
      setReportSubTitle(subtitleFromState);
    }
    // Only fetch if not KPI dependent, or if KPI is already selected
    if (!isKpiDependent || selectedKpiId) {
      fetchReportData();
    }
  }, [inputID, reportId, reportCode, location.state, selectedKpiId]);

  useEffect(() => {
    if (!isKpiDependent) return;

    const fetchKpiDetails = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("Unauthorized access. Please log in again.");

      const response = await fetch(
        `${BASE_URL}/kpi_project/get_kpis_of_project`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ project_id: projectId }),
        },
      );

      if (response.ok) {
        const kpiData = await response.json();
        setKpiList(kpiData);
        if (kpiData.length > 0) {
          setSelectedKpiId(kpiData[0].id);
        }
      }
    };

    fetchKpiDetails();
  }, [isKpiDependent]);

  useEffect(() => {
    if (activeTab === "visual") {
      fetchVisualData(selectedKpiId);
    }
  }, [activeTab, selectedKpiId]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const params = new URLSearchParams({
        input_id: inputID,
        report_code: reportCode,
        report_id: reportId,
      });

      if (isKpiDependent && selectedKpiId) {
        params.append("kpi_id", selectedKpiId);
      }

      const response = await fetch(
        `${BASE_URL}/reports/get_report_data_kpi?${params.toString()}&mart_id=${mart_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Report data response:", result);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const InsightsEditor = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(
      "A design system for enterprise-level products. Create an efficient and enjoyable work experience.",
    );
    const [tempText, setTempText] = useState(text);

    const handleEdit = () => {
      setTempText(text);
      setIsEditing(true);
    };

    const handleSave = () => {
      setText(tempText);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setTempText(text);
      setIsEditing(false);
    };

    return (
      <div className="reportview-insight-container">
        <div className="reportview-insight-header">
          <h2 className="reportview-insight-title">Insights</h2>
          {!isEditing && (
            <button
              className="reportview-insight-edit-btn"
              onClick={handleEdit}
            >
              <Edit2 size={16} />
            </button>
          )}
        </div>

        {!isEditing ? (
          <div className="reportview-insight-content">
            <p className="reportview-insight-text">{text}</p>
          </div>
        ) : (
          <div className="reportview-insight-edit-section">
            <textarea
              className="reportview-insight-textarea"
              value={tempText}
              onChange={(e) => setTempText(e.target.value)}
              placeholder="Enter your insights..."
            />
            <div className="reportview-insight-actions">
              <button
                className="reportview-insight-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="reportview-insight-save-btn"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const fetchVisualData = async (kpiId = selectedKpiId) => {
    // ✅ accept as param with default
    try {
      setVisualLoading(true);
      setVisualError(null);
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("Unauthorized access. Please log in again.");

      const firstApiResponse = await fetch(
        `${BASE_URL}/reports/get_all_questions_under_report?report_id=${reportId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!firstApiResponse.ok) {
        throw new Error(`HTTP error! status: ${firstApiResponse.status}`);
      }

      const questions = await firstApiResponse.json();

      const visualDataPromises = questions.map(async (question) => {
        const secondApiResponse = await fetch(
          `${BASE_URL}/reports/get_data_for_visuals_kpi?question_id=${question.id}&input_id=${inputID}&report_id=${reportId}&mart_id=${mart_id}${isKpiDependent && kpiId ? `&kpi_id=${kpiId}` : ""}`, // ✅ use kpiId param
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!secondApiResponse.ok) {
          return {
            question: question.question,
            data: null,
            error: `HTTP error! status: ${secondApiResponse.status}`,
          };
        }

        const data = await secondApiResponse.json();
        return { question: question.question, data };
      });

      const visualDataResults = await Promise.all(visualDataPromises);
      setVisualData(visualDataResults.filter((result) => !result.error));
    } catch (err) {
      setVisualError(err.message);
    } finally {
      setVisualLoading(false);
    }
  };

  const parseDataStructure = (data) => {
    if (!data || typeof data !== "object") return null;

    const keys = Object.keys(data);
    if (keys.length === 0) return null;

    const firstKey = keys[0];
    const firstValue = data[firstKey];

    let depth = 0;
    let current = firstValue;

    while (current && typeof current === "object" && !Array.isArray(current)) {
      const subKeys = Object.keys(current);
      if (subKeys.length === 0) break;

      const firstSubKey = subKeys[0];
      const firstSubValue = current[firstSubKey];

      if (typeof firstSubValue === "object" && !Array.isArray(firstSubValue)) {
        depth++;
        current = firstSubValue;
      } else {
        depth++;
        break;
      }
    }

    return { depth, keys, data };
  };

  // const isSimpleCategoryObjectData = (data) => {
  //   // Check if data is a simple category object structure with scalar values
  //   // e.g., { "CAP 9": { "S No": 1, "Cohort": "...", ...}, "CAP 12": {...} }
  //   if (!data || typeof data !== "object" || Array.isArray(data)) return false;

  //   const keys = Object.keys(data);
  //   if (keys.length === 0) return false;

  //   // Check if all values are objects with ALL scalar values (no nested objects)
  //   return keys.every((key) => {
  //     const value = data[key];
  //     if (typeof value !== "object" || value === null || Array.isArray(value))
  //       return false;

  //     const subKeys = Object.keys(value);
  //     if (subKeys.length === 0) return false;

  //     // Check if all sub-values are scalars (not objects)
  //     return subKeys.every((subKey) => {
  //       const subValue = value[subKey];
  //       return subValue === null || typeof subValue !== "object";
  //     });
  //   });
  // };

  // const renderSimpleCategoryObjectTable = (data) => {
  //   const categories = Object.keys(data);

  //   // Collect all property keys from all categories
  //   const allProperties = new Set();
  //   categories.forEach((category) => {
  //     if (data[category] && typeof data[category] === "object") {
  //       Object.keys(data[category]).forEach((prop) => allProperties.add(prop));
  //     }
  //   });

  //   const properties = Array.from(allProperties);

  //   return (
  //     <>
  //       <div className="reportview-container">
  //         <div className="rv-scroll-top">
  //           <table className="reportview-table">
  //             <thead>
  //               <tr>
  //                 <th className="reportview-header reportview-row-header">
  //                   Property
  //                 </th>
  //                 {categories.map((category) => (
  //                   <th
  //                     key={category}
  //                     className="reportview-header reportview-col-header"
  //                   >
  //                     {category}
  //                   </th>
  //                 ))}
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {properties.map((prop) => (
  //                 <tr key={prop} className="reportview-row">
  //                   <td className="reportview-cell reportview-row-label">
  //                     {prop}
  //                   </td>
  //                   {categories.map((category) => (
  //                     <td
  //                       key={`${prop}-${category}`}
  //                       className="reportview-cell reportview-data-cell"
  //                     >
  //                       {data[category] && data[category][prop] !== undefined
  //                         ? formatCellValue(data[category][prop])
  //                         : "-"}
  //                     </td>
  //                   ))}
  //                 </tr>
  //               ))}
  //             </tbody>
  //           </table>
  //         </div>
  //       </div>
  //       <InsightsEditor />
  //     </>
  //   );
  // };

  const renderTable = () => {
    if (!data) {
      return <div className="reportview-no-data">No data available</div>;
    }

    if (decodeURIComponent(reportCode) === "SUP 1") {
      return renderSupPlusOneTable(data);
    }

    if (decodeURIComponent(reportCode) === "PER 20") {
      return renderPerTwentyTable(data);
    }

    if (decodeURIComponent(reportCode) === "COR 1") {
      return renderCOR1Table(data);
    }

    if (decodeURIComponent(reportCode) === "COMP 4") {
      return renderCOMP4Table(data);
    }

    if (isCohortLRMData(data)) {
      return renderCohortLRMTable(data);
    }

    const keys = Object.keys(data);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const firstValue = data[firstKey];

      // Check if all values are objects with numeric string keys
      const isFlatJson = keys.every((key) => {
        const value = data[key];
        // console.log(value);
        if (typeof value !== "object" || value === null) return false;

        const subKeys = Object.keys(value);
        // console.log(subKeys.every((subKey) => !isNaN(parseInt(subKey))));
        return subKeys.every((subKey) => !isNaN(parseInt(subKey)));
      });

      if (isFlatJson) {
        return renderFlatJsonTable(data);
      }
    }

    const structure = parseDataStructure(data);
    if (!structure) {
      return <div className="reportview-no-data">No data available</div>;
    }

    const { depth, data: tableData } = structure;

    if (depth === 1) {
      return renderTwoKeyTable(tableData);
    } else if (depth === 2) {
      return renderThreeKeyTable(tableData);
    } else if (depth === 3) {
      return renderFourKeyTable(tableData);
    }

    return <div className="reportview-error">Unsupported data structure</div>;
  };

  const renderComplexTable = (tableData) => {
    const sections = Object.keys(tableData);

    return (
      <div className="reportview-container">
        {sections.map((section, sectionIndex) => (
          <div key={section} className="reportview-section">
            <div className="reportview-section-header">{section}</div>
            <div className="rv-scroll-top">
              {renderSectionTable(tableData[section], section)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSectionTable = (sectionData, sectionName) => {
    const rows = Object.keys(sectionData);
    const allColumns = new Set();

    rows.forEach((row) => {
      if (sectionData[row] && typeof sectionData[row] === "object") {
        Object.keys(sectionData[row]).forEach((col) => allColumns.add(col));
      }
    });

    const columns = Array.from(allColumns);

    return (
      <table className="reportview-table reportview-section-table">
        <thead>
          <tr>
            <th className="reportview-header reportview-row-header">
              {sectionName}
            </th>
            {columns.map((col) => (
              <th key={col} className="reportview-header reportview-col-header">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row} className="reportview-row">
              <td className="reportview-cell reportview-row-label">{row}</td>
              {columns.map((col) => (
                <td
                  key={`${row}-${col}`}
                  className="reportview-cell reportview-data-cell"
                >
                  {sectionData[row] && sectionData[row][col] !== undefined
                    ? formatCellValue(sectionData[row][col])
                    : "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      if (value % 1 === 0) {
        return value.toLocaleString();
      } else {
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
      }
    }
    return value;
  };

  const isEmpIdColumn = (metricName) =>
    typeof metricName === "string" && metricName.toLowerCase().includes("emp id");

  const renderEmpIdCell = (value) => {
    if (value === null || value === undefined) return "-";
    const str = String(value);
    const ids = str.split(",").map((s) => s.trim()).filter(Boolean);
    if (ids.length <= 1) return str;
    return (
      <span>
        {ids.map((id, i) => (
          <span key={i} style={{ display: "block", whiteSpace: "nowrap" }}>
            {id}
          </span>
        ))}
      </span>
    );
  };

  const roundToTwo = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    return Math.round(num * 100) / 100;
  };

  const extractSupPlusOneCellValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value !== "object" || Array.isArray(value)) return value;
    const keys = Object.keys(value);
    // API wraps cell values as { <anyKey>: actualValue }
    if (keys.length === 1) {
      return value[keys[0]];
    }
    return value;
  };

  const isPerTwentyMetricRow = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const entries = Object.values(value);
    if (entries.length === 0) return false;
    return entries.every(
      (entry) =>
        entry === null ||
        entry === undefined ||
        typeof entry !== "object" ||
        Array.isArray(entry),
    );
  };

  const flattenPerTwentySectionRows = (sectionData, groupLabel = "") => {
    const rows = [];
    if (!sectionData || typeof sectionData !== "object") return rows;

    Object.entries(sectionData).forEach(([key, value]) => {
      if (isPerTwentyMetricRow(value)) {
        rows.push({ group: groupLabel, metric: key, values: value });
        return;
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        const nextGroup = groupLabel ? groupLabel : key;
        rows.push(...flattenPerTwentySectionRows(value, nextGroup));
      }
    });

    return rows;
  };

  const collectPerTwentyColumns = (rows) => {
    const columns = new Set();
    rows.forEach((row) => {
      Object.keys(row.values || {}).forEach((column) => columns.add(column));
    });
    return Array.from(columns);
  };

  const isPerTwentyExtentSection = (sectionData) => {
    if (!sectionData || typeof sectionData !== "object") return false;
    return Object.values(sectionData).some(
      (value) =>
        value &&
        typeof value === "object" &&
        !isPerTwentyMetricRow(value) &&
        Object.values(value).some((nested) => isPerTwentyMetricRow(nested)),
    );
  };

  const buildPerTwentyExtentColumnDefs = (sectionData) => {
    const columnDefs = [];

    Object.entries(sectionData).forEach(([group, groupData]) => {
      if (!groupData || typeof groupData !== "object") return;

      Object.entries(groupData).forEach(([metric, values]) => {
        if (!isPerTwentyMetricRow(values)) return;
        columnDefs.push({
          group,
          metric,
          key: `${group}||${metric}`,
          values,
        });
      });
    });

    return columnDefs;
  };

  const collectPerTwentyExtentRows = (columnDefs) => {
    const rows = new Set();
    columnDefs.forEach((column) => {
      Object.keys(column.values || {}).forEach((row) => rows.add(row));
    });
    return Array.from(rows);
  };

  const renderPerTwentyExtentSectionTable = (sectionTitle, sectionData) => {
    const columnDefs = buildPerTwentyExtentColumnDefs(sectionData);
    const rowLabels = collectPerTwentyExtentRows(columnDefs);

    const groupColspans = [];
    let columnIndex = 0;
    while (columnIndex < columnDefs.length) {
      const group = columnDefs[columnIndex].group;
      let span = 0;
      while (
        columnIndex + span < columnDefs.length &&
        columnDefs[columnIndex + span].group === group
      ) {
        span++;
      }
      groupColspans.push({ group, colSpan: span, key: group });
      columnIndex += span;
    }

    return (
      <div key={sectionTitle} className="reportview-section">
        <div className="reportview-section-header">{sectionTitle}</div>
        <div className="rv-scroll-top">
          <table className="reportview-table reportview-section-table">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className="reportview-header reportview-row-header"
                />
                {groupColspans.map((group) => (
                  <th
                    key={group.key}
                    colSpan={group.colSpan}
                    className="reportview-header sup-plus-one-section-header"
                  >
                    {group.group}
                  </th>
                ))}
              </tr>
              <tr>
                {columnDefs.map((column) => (
                  <th
                    key={column.key}
                    className="reportview-header reportview-col-header sup-plus-one-metric-header"
                  >
                    {column.metric}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowLabels.map((rowLabel) => (
                <tr key={rowLabel} className="reportview-row">
                  <td className="reportview-cell reportview-row-label">
                    {rowLabel}
                  </td>
                  {columnDefs.map((column) => (
                    <td
                      key={`${rowLabel}-${column.key}`}
                      className="reportview-cell reportview-data-cell"
                    >
                      {column.values && column.values[rowLabel] !== undefined
                        ? formatCellValue(column.values[rowLabel])
                        : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPerTwentySectionTable = (sectionTitle, sectionData) => {
    if (isPerTwentyExtentSection(sectionData)) {
      return renderPerTwentyExtentSectionTable(sectionTitle, sectionData);
    }

    const rows = flattenPerTwentySectionRows(sectionData);
    const columns = collectPerTwentyColumns(rows);
    const uniqueGroups = [
      ...new Set(rows.map((row) => row.group).filter(Boolean)),
    ];
    const showGroupColumn = uniqueGroups.length > 1;

    return (
      <div key={sectionTitle} className="reportview-section">
        <div className="reportview-section-header">{sectionTitle}</div>
        <div className="rv-scroll-top">
          <table className="reportview-table reportview-section-table">
            <thead>
              <tr>
                {showGroupColumn && (
                  <th className="reportview-header reportview-row-header">
                    Category
                  </th>
                )}
                <th className="reportview-header reportview-row-header">
                  Metric
                </th>
                {columns.map((column) => (
                  <th
                    key={column}
                    className="reportview-header reportview-col-header"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.group}-${row.metric}`}
                  className="reportview-row"
                >
                  {showGroupColumn && (
                    <td className="reportview-cell reportview-row-label">
                      {row.group}
                    </td>
                  )}
                  <td className="reportview-cell reportview-row-label">
                    {row.metric}
                  </td>
                  {columns.map((column) => (
                    <td
                      key={`${row.metric}-${column}`}
                      className="reportview-cell reportview-data-cell"
                    >
                      {row.values && row.values[column] !== undefined
                        ? formatCellValue(row.values[column])
                        : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPerTwentyTable = (inputData) => {
    const sections = Object.keys(inputData || {});
    if (sections.length === 0) {
      return <div className="reportview-no-data">No data available</div>;
    }

    return (
      <>
        <div className="reportview-container">
          {sections.map((section) =>
            renderPerTwentySectionTable(section, inputData[section]),
          )}
        </div>
        <InsightsEditor />
      </>
    );
  };

  const isSectionMetricObject = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const metrics = Object.values(value);
    if (metrics.length === 0) return false;
    return metrics.every(
      (metricValue) =>
        metricValue &&
        typeof metricValue === "object" &&
        !Array.isArray(metricValue),
    );
  };

  const isSupPlusOneScalar = (value) =>
    value === null || value === undefined || typeof value !== "object";

  // Wide format: section → metric → { L9256: value, L11217: value, ... }
  const isSupPlusOneWideFormat = (inputData) => {
    if (!inputData || typeof inputData !== "object" || Array.isArray(inputData)) {
      return false;
    }
    if (!isSectionMetricObject(inputData)) return false;

    for (const section of Object.values(inputData)) {
      for (const metricValue of Object.values(section)) {
        if (!metricValue || typeof metricValue !== "object" || Array.isArray(metricValue)) {
          continue;
        }
        const keys = Object.keys(metricValue);
        if (keys.length <= 1) continue;
        if (Object.values(metricValue).every(isSupPlusOneScalar)) {
          return true;
        }
      }
    }
    return false;
  };

  const collectSupPlusOneSupervisorIds = (inputData) => {
    const supervisorSection = inputData["Supervisor details"];
    if (supervisorSection && typeof supervisorSection === "object") {
      const empIdMetric =
        supervisorSection["Emp ID of Immediate Supervisor"] ||
        Object.entries(supervisorSection).find(([key]) =>
          key.toLowerCase().includes("emp id"),
        )?.[1];
      if (empIdMetric && typeof empIdMetric === "object" && !Array.isArray(empIdMetric)) {
        return Object.keys(empIdMetric);
      }
    }

    const seen = new Set();
    const ordered = [];
    Object.values(inputData).forEach((section) => {
      if (!section || typeof section !== "object" || Array.isArray(section)) return;
      Object.values(section).forEach((metricValue) => {
        if (!metricValue || typeof metricValue !== "object" || Array.isArray(metricValue)) {
          return;
        }
        Object.keys(metricValue).forEach((supervisorId) => {
          if (!seen.has(supervisorId)) {
            seen.add(supervisorId);
            ordered.push(supervisorId);
          }
        });
      });
    });
    return ordered;
  };

  const buildSupPlusOneColumnDefs = (payload) => {
    const sections = Object.keys(payload).filter(
      (section) =>
        payload[section] &&
        typeof payload[section] === "object" &&
        !Array.isArray(payload[section]),
    );

    const columnDefs = [];
    sections.forEach((section) => {
      Object.keys(payload[section]).forEach((metric) => {
        columnDefs.push({ section, metric });
      });
    });

    const sectionColspans = sections.map((section) => ({
      section,
      colSpan: columnDefs.filter((col) => col.section === section).length,
    }));

    return { sections, columnDefs, sectionColspans };
  };

  const getSupPlusOneWideCellValue = (payload, section, metric, supervisorId) => {
    const rawValue = payload?.[section]?.[metric]?.[supervisorId];
    return extractSupPlusOneCellValue(rawValue);
  };

  const normalizeSupPlusOneRows = (inputData) => {
    if (!inputData || typeof inputData !== "object" || Array.isArray(inputData)) {
      return [];
    }
    const topEntries = Object.entries(inputData);
    if (topEntries.length === 0) return [];

    // Legacy single-row wrapped cells: { "Emp ID": { not: "x" }, ... }
    if (isSectionMetricObject(topEntries[0][1]) && !isSupPlusOneWideFormat(inputData)) {
      return [{ rowLabel: "1", payload: inputData }];
    }

    // Multi row payload shape:
    // { "0": { "Supervisor details": {...}, ... }, "1": {...} }
    return topEntries
      .filter(
        ([, value]) => value && typeof value === "object" && !Array.isArray(value),
      )
      .map(([rowLabel, payload]) => ({ rowLabel, payload }));
  };

  const renderSupPlusOneWideTable = (inputData) => {
    const supervisorIds = collectSupPlusOneSupervisorIds(inputData);
    if (supervisorIds.length === 0) {
      return <div className="reportview-no-data">No data available</div>;
    }

    const { columnDefs, sectionColspans } = buildSupPlusOneColumnDefs(inputData);

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table sup-plus-one-table">
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    className="reportview-header reportview-row-header sup-plus-one-row-header"
                  >
                    Supervisor ID
                  </th>
                  {sectionColspans.map((group) => (
                    <th
                      key={group.section}
                      colSpan={group.colSpan}
                      className="reportview-header sup-plus-one-section-header"
                    >
                      {group.section}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnDefs.map((column) => (
                    <th
                      key={`${column.section}||${column.metric}`}
                      className="reportview-header reportview-col-header sup-plus-one-metric-header"
                    >
                      {column.metric}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supervisorIds.map((supervisorId) => (
                  <tr key={supervisorId} className="reportview-row">
                    <td className="reportview-cell reportview-row-header sup-plus-one-row-header">
                      {supervisorId}
                    </td>
                    {columnDefs.map((column) => {
                      const value = getSupPlusOneWideCellValue(
                        inputData,
                        column.section,
                        column.metric,
                        supervisorId,
                      );
                      return (
                        <td
                          key={`${supervisorId}-${column.section}-${column.metric}`}
                          className="reportview-cell reportview-data-cell"
                        >
                          {isEmpIdColumn(column.metric)
                            ? renderEmpIdCell(value)
                            : formatCellValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderSupPlusOneLegacyTable = (inputData) => {
    const rows = normalizeSupPlusOneRows(inputData);
    if (rows.length === 0) {
      return <div className="reportview-no-data">No data available</div>;
    }

    const firstRowPayload = rows[0].payload;
    const { columnDefs, sectionColspans } = buildSupPlusOneColumnDefs(firstRowPayload);

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table sup-plus-one-table">
              <thead>
                <tr>
                  {sectionColspans.map((group) => (
                    <th
                      key={group.section}
                      colSpan={group.colSpan}
                      className="reportview-header sup-plus-one-section-header"
                    >
                      {group.section}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnDefs.map((column) => (
                    <th
                      key={`${column.section}||${column.metric}`}
                      className="reportview-header reportview-col-header sup-plus-one-metric-header"
                    >
                      {column.metric}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`${row.rowLabel}-${rowIndex}`} className="reportview-row">
                    {columnDefs.map((column) => {
                      const rawValue = row.payload?.[column.section]?.[column.metric];
                      const value = extractSupPlusOneCellValue(rawValue);
                      return (
                        <td
                          key={`${row.rowLabel}-${column.section}-${column.metric}`}
                          className="reportview-cell reportview-data-cell"
                        >
                          {isEmpIdColumn(column.metric)
                            ? renderEmpIdCell(value)
                            : formatCellValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderSupPlusOneTable = (inputData) => {
    if (!inputData || typeof inputData !== "object" || Array.isArray(inputData)) {
      return <div className="reportview-no-data">No data available</div>;
    }

    if (isSupPlusOneWideFormat(inputData)) {
      return renderSupPlusOneWideTable(inputData);
    }

    return renderSupPlusOneLegacyTable(inputData);
  };

  // ─── COR 1: Slab-based grouped-column table ───────────────────────────────

  const renderCOR1Table = (inputData) => {
    const sections = Object.keys(inputData);
    if (sections.length === 0)
      return <div className="reportview-no-data">No data available</div>;

    const sectionColumns = sections.map((section) => ({
      section,
      columns: Object.keys(inputData[section]),
    }));

    const firstSectionData = inputData[sections[0]];
    const firstColKey = Object.keys(firstSectionData)[0];
    const rowIndices = Object.keys(firstSectionData[firstColKey]).sort(
      (a, b) => parseInt(a) - parseInt(b),
    );

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table cor1-table">
              <thead>
                <tr>
                  <th className="cor1-corner" rowSpan={2}>
                    Slab #
                  </th>
                  {sectionColumns.map(({ section, columns }) => (
                    <th
                      key={section}
                      className="cor1-section-header"
                      colSpan={columns.length}
                    >
                      {section}
                    </th>
                  ))}
                </tr>
                <tr>
                  {sectionColumns.map(({ section, columns }) =>
                    columns.map((col) => (
                      <th key={`${section}-${col}`} className="cor1-col-header">
                        {col}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {rowIndices.map((rowIdx, i) => (
                  <tr key={rowIdx} className="reportview-row">
                    <td className="reportview-cell cor1-slab-num">{i + 1}</td>
                    {sectionColumns.map(({ section, columns }) =>
                      columns.map((col) => {
                        const val = inputData[section]?.[col]?.[rowIdx];
                        return (
                          <td
                            key={`${section}-${col}-${rowIdx}`}
                            className="reportview-cell reportview-data-cell"
                          >
                            {formatCellValue(val)}
                          </td>
                        );
                      }),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  // ─── COMP 4: Base-columns + KPI grouped-column table ─────────────────────

  const renderCOMP4Table = (inputData) => {
    const allKeys = Object.keys(inputData);
    if (allKeys.length === 0)
      return <div className="reportview-no-data">No data available</div>;

    // "data" key holds the base (ungrouped) columns; all others are KPI sections
    const BASE_KEY = "data";
    const baseColumns = inputData[BASE_KEY]
      ? Object.keys(inputData[BASE_KEY])
      : [];
    const kpiSections = allKeys
      .filter((k) => k !== BASE_KEY)
      .map((section) => ({
        section,
        columns: Object.keys(inputData[section]),
      }));

    // Row indices from the first available column
    const firstSectionData =
      inputData[BASE_KEY] || inputData[allKeys[0]];
    const firstColKey = Object.keys(firstSectionData)[0];
    const rowIndices = Object.keys(firstSectionData[firstColKey]).sort(
      (a, b) => parseInt(a) - parseInt(b),
    );

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table">
              <thead>
                {/* Row 1: base cols span both header rows; KPI group headers */}
                <tr>
                  {baseColumns.map((col) => (
                    <th
                      key={col}
                      className="comp4-col-header"
                      rowSpan={2}
                    >
                      {col}
                    </th>
                  ))}
                  {kpiSections.map(({ section, columns }) => (
                    <th
                      key={section}
                      className="comp4-kpi-section-header"
                      colSpan={columns.length}
                    >
                      {section}
                    </th>
                  ))}
                </tr>
                {/* Row 2: KPI sub-column headers only */}
                <tr>
                  {kpiSections.map(({ section, columns }) =>
                    columns.map((col) => (
                      <th
                        key={`${section}-${col}`}
                        className="comp4-col-header"
                      >
                        {col}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {rowIndices.map((rowIdx) => (
                  <tr key={rowIdx} className="reportview-row">
                    {baseColumns.map((col) => {
                      const val = inputData[BASE_KEY]?.[col]?.[rowIdx];
                      return (
                        <td
                          key={`base-${col}-${rowIdx}`}
                          className="reportview-cell reportview-data-cell"
                        >
                          {formatCellValue(val)}
                        </td>
                      );
                    })}
                    {kpiSections.map(({ section, columns }) =>
                      columns.map((col) => {
                        const val = inputData[section]?.[col]?.[rowIdx];
                        return (
                          <td
                            key={`${section}-${col}-${rowIdx}`}
                            className="reportview-cell reportview-data-cell"
                          >
                            {formatCellValue(val)}
                          </td>
                        );
                      }),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  // ─── Cohort LRM hierarchical table helpers ───────────────────────────────

  const isCohortLRMData = (inputData) => {
    if (!inputData || typeof inputData !== "object") return false;
    const topKeys = Object.keys(inputData);
    if (topKeys.length === 0) return false;
    const firstTopValue = inputData[topKeys[0]];
    if (!firstTopValue || typeof firstTopValue !== "object") return false;
    const subKeys = Object.keys(firstTopValue);
    if (subKeys.length === 0) return false;
    const firstSubValue = firstTopValue[subKeys[0]];
    if (!firstSubValue || typeof firstSubValue !== "object") return false;
    const sectionKeys = Object.keys(firstSubValue);
    return (
      sectionKeys.includes("HEAD COUNT") ||
      sectionKeys.includes("Attrition Indicators")
    );
  };

  const extractCohortValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return value;
    if (typeof value !== "object") return null;
    if (!("UPTO LRM" in value)) return null;
    const uptoLRM = value["UPTO LRM"];
    if (uptoLRM === null || typeof uptoLRM === "number") return uptoLRM;
    if (typeof uptoLRM === "object" && uptoLRM !== null && "UPTO LRM" in uptoLRM) {
      return uptoLRM["UPTO LRM"];
    }
    return null;
  };

  const renderCohortLRMTable = (inputData) => {
    // Collect reference sub-group data (first sub-group of first top-group)
    let refSubData = null;
    outerLoop: for (const topKey of Object.keys(inputData)) {
      for (const subKey of Object.keys(inputData[topKey])) {
        refSubData = inputData[topKey][subKey];
        break outerLoop;
      }
    }
    if (!refSubData) return <div className="reportview-no-data">No data</div>;

    // True when a section's immediate keys are residency months
    // (HEAD COUNT: section → "Residency Month Rx" → scalar)
    const isTimeSeries = (sectionData) =>
      Object.keys(sectionData).some((k) => /^Residency Month R\d+$/.test(k));

    // Build a flat list of column descriptors from the reference sub-group.
    //
    // Column types:
    //   'timeSeries'    – HEAD COUNT style: section → residencyMonth → scalar
    //                     col = residency month key
    //   'expandedMetric'– Performance KPI style: section → metricName → residencyMonth → scalar
    //                     col = residency month key, metricName = parent metric name
    //   'scalarMetric'  – Attrition / Retention etc.: section → metricName → scalar
    //                     col = metric name
    const columnDefs = [];
    for (const section of Object.keys(refSubData)) {
      const sectionData = refSubData[section];
      if (!sectionData || typeof sectionData !== "object") continue;
      const firstVal = sectionData[Object.keys(sectionData)[0]];

      if (isTimeSeries(sectionData)) {
        // 2-level time-series: each residency month is its own column
        for (const k of Object.keys(sectionData)) {
          columnDefs.push({ type: "timeSeries", section, col: k });
        }
      } else if (firstVal && typeof firstVal === "object") {
        // 3-level: expand each metric × residency month into its own column
        for (const metricName of Object.keys(sectionData)) {
          for (const resMonth of Object.keys(sectionData[metricName])) {
            columnDefs.push({
              type: "expandedMetric",
              section,
              metricName,
              col: resMonth,
            });
          }
        }
      } else {
        // 2-level scalar metrics (Attrition, Retention, Under-performance)
        for (const k of Object.keys(sectionData)) {
          columnDefs.push({ type: "scalarMetric", section, col: k });
        }
      }
    }

    // Build display rows
    const rows = [];
    for (const topKey of Object.keys(inputData)) {
      const subGroups = Object.keys(inputData[topKey]);
      if (subGroups.length === 1) {
        rows.push({
          label: topKey,
          indent: 0,
          data: inputData[topKey][subGroups[0]],
          isHeader: false,
        });
      } else {
        rows.push({ label: topKey, indent: 0, data: null, isHeader: true });
        for (const subKey of subGroups) {
          rows.push({
            label: subKey,
            indent: 1,
            data: inputData[topKey][subKey],
            isHeader: false,
          });
        }
      }
    }

    // Extract display value for a cell.
    // Handles the deeper nesting that occurs in sub-groups where Performance KPI
    // values are triply nested: metricData[resMonth][resMonth] = scalar.
    const getCellValue = (rowData, colDef) => {
      if (!rowData || !rowData[colDef.section]) return null;
      const secData = rowData[colDef.section];

      if (colDef.type === "timeSeries") {
        const val = secData[colDef.col];
        return typeof val === "number" ? val : null;
      }

      if (colDef.type === "expandedMetric") {
        const metricData = secData[colDef.metricName];
        if (!metricData || typeof metricData !== "object") return null;
        const val = metricData[colDef.col];
        if (val === null || val === undefined) return null;
        if (typeof val === "number") return val;
        // Sub-groups have an extra nesting level: val[col] = scalar
        if (typeof val === "object") {
          const deep = val[colDef.col];
          return typeof deep === "number" ? deep : null;
        }
        return null;
      }

      // scalarMetric – use extractCohortValue so it still works when a
      // sub-group happens to store an object with "UPTO LRM" instead of a scalar
      return extractCohortValue(secData[colDef.col]);
    };

    // ── Build multi-level table headers ──────────────────────────────────────
    //
    // Row 1 : section names (colspan = total cols in that section)
    // Row 2 : • timeSeries / scalarMetric columns → column name, rowSpan=2
    //         • expandedMetric columns → metric name, colspan = #resMonths
    // Row 3 : expandedMetric columns only → residency month name
    //         (row 3 is omitted entirely when there are no expandedMetric cols)

    const hasExpanded = columnDefs.some((c) => c.type === "expandedMetric");
    const labelRowSpan = hasExpanded ? 3 : 2;

    // Section colspans for row 1
    const sectionColspan = {};
    for (const c of columnDefs) {
      sectionColspan[c.section] = (sectionColspan[c.section] || 0) + 1;
    }
    const uniqueSections = Object.keys(sectionColspan);

    // Row-2 header cells
    const row2Cells = [];
    let ci = 0;
    while (ci < columnDefs.length) {
      const c = columnDefs[ci];
      if (c.type === "expandedMetric") {
        // Group consecutive cols with same section + metricName
        let span = 0;
        while (
          ci + span < columnDefs.length &&
          columnDefs[ci + span].type === "expandedMetric" &&
          columnDefs[ci + span].section === c.section &&
          columnDefs[ci + span].metricName === c.metricName
        ) {
          span++;
        }
        row2Cells.push({
          label: c.metricName,
          colSpan: span,
          rowSpan: 1,
          key: `${c.section}||${c.metricName}`,
        });
        ci += span;
      } else {
        row2Cells.push({
          label: c.col,
          colSpan: 1,
          rowSpan: hasExpanded ? 2 : 1,
          key: `${c.section}||${c.col}`,
        });
        ci++;
      }
    }

    // Row-3 header cells (expandedMetric only)
    const row3Cells = columnDefs
      .filter((c) => c.type === "expandedMetric")
      .map((c) => ({ label: c.col, key: `${c.section}||${c.metricName}||${c.col}` }));

    return (
      <div className="rv-scroll-top">
        <table className="reportview-table cohort-lrm-table">
          <thead>
            <tr>
              <th
                rowSpan={labelRowSpan}
                className="reportview-header cohort-input-vars-header"
              >
                Input Variables
              </th>
              {uniqueSections.map((section) => (
                <th
                  key={section}
                  colSpan={sectionColspan[section]}
                  className="reportview-header cohort-section-header"
                >
                  {section}
                </th>
              ))}
            </tr>
            <tr>
              {row2Cells.map((cell) => (
                <th
                  key={cell.key}
                  colSpan={cell.colSpan}
                  rowSpan={cell.rowSpan}
                  className="reportview-header reportview-col-header cohort-metric-header"
                >
                  {cell.label}
                </th>
              ))}
            </tr>
            {hasExpanded && (
              <tr>
                {row3Cells.map((cell) => (
                  <th
                    key={cell.key}
                    className="reportview-header reportview-col-header cohort-metric-header"
                  >
                    {cell.label}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={idx}
                className={
                  row.isHeader
                    ? "reportview-row cohort-group-header-row"
                    : "reportview-row"
                }
              >
                <td
                  className={`reportview-cell reportview-row-label${row.isHeader ? " cohort-group-label" : ""}${row.indent > 0 ? " cohort-sub-label" : ""}`}
                  style={{ paddingLeft: `${row.indent * 20 + 12}px` }}
                >
                  {row.label}
                </td>
                {row.isHeader ? (
                  <td
                    colSpan={columnDefs.length}
                    className="reportview-cell"
                    style={{ background: "#f0f4ff" }}
                  />
                ) : (
                  columnDefs.map((colDef, cIdx) => (
                    <td
                      key={cIdx}
                      className="reportview-cell reportview-data-cell"
                    >
                      {formatCellValue(getCellValue(row.data, colDef))}
                    </td>
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────

  const parseChartNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") {
      const coerced = Number(value);
      return Number.isFinite(coerced) ? coerced : null;
    }

    const trimmed = value.trim();
    if (!trimmed) return null;

    // Handle accounting format and unicode minus from API text payloads.
    const normalized = trimmed
      .replace(/\u2212/g, "-")
      .replace(/,/g, "")
      .replace(/^\((.*)\)$/, "-$1");

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatChartNumber = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    if (Number.isInteger(num)) return num.toLocaleString();
    return roundToTwo(num).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Generate perfectly consistent axis ticks when the API does not supply
  // explicit gridlines.  Uses ceil-to-magnitude rounding so the step matches
  // what Recharts would naturally pick (e.g. dataMax=97 → step=30 →
  // ticks=[0,30,60,90,120]) but never leaves an orphan "nice ceiling" label.
  const computeNiceTicks = (dataMax, targetIntervals = 4) => {
    if (!dataMax || dataMax <= 0) return { ticks: [0], domainMax: 10 };
    const rawStep = dataMax / targetIntervals;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step = Math.ceil(rawStep / magnitude) * magnitude;
    const domainMax = Math.ceil(dataMax / step) * step;
    const ticks = [];
    for (let t = 0; t <= domainMax + step * 0.001; t += step) {
      ticks.push(Math.round(t * 1e9) / 1e9);
    }
    return { ticks, domainMax };
  };

  const isMetricsData = (data) => {
    // Check if data is a simple key-value object where all values are numbers
    if (!data || typeof data !== "object" || Array.isArray(data)) return false;

    const keys = Object.keys(data);
    if (keys.length === 0) return false;

    // Check if all values are numbers (not objects)
    const isMetrics = keys.every((key) => typeof data[key] === "number");
    console.log("isMetricsData check:", { data, keys, isMetrics });
    return isMetrics;
  };

  const renderMetricsTable = (data) => {
    const metrics = Object.entries(data).map(([label, value]) => ({
      label,
      value,
    }));

    console.log("renderMetricsTable metrics:", metrics);

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table" style={{ tableLayout: "fixed", minWidth: "auto" }}>
              <colgroup>
                <col style={{ width: "60%" }} />
                <col style={{ width: "40%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="reportview-header reportview-col-header" style={{ textAlign: "left" }}>
                    Metric
                  </th>
                  <th className="reportview-header reportview-col-header">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric, index) => {
                  console.log(`Rendering metric ${index}:`, metric.label, "=>", metric.value);
                  return (
                    <tr key={index} className="reportview-row">
                      <td className="reportview-cell reportview-row-label" style={{ wordWrap: "break-word", whiteSpace: "normal", padding: "12px" }}>
                        {metric.label}
                      </td>
                      <td className="reportview-cell reportview-data-cell" style={{ textAlign: "right", padding: "12px" }}>
                        {formatCellValue(metric.value)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderFlatJsonTable = (data) => {
    // Get all column headers (keys from the JSON)
    const columns = Object.keys(data);

    // Get all row indices by examining the first column's keys
    const firstColumn = data[columns[0]];
    const rowIndices = Object.keys(firstColumn)
      .map((key) => parseInt(key))
      .sort((a, b) => a - b);

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    // console.log(columns),
                    <th
                      key={column}
                      className="reportview-header reportview-col-header"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowIndices.map((rowIndex) => (
                  <tr key={rowIndex} className="reportview-row">
                    {columns.map((column) => (
                      <td
                        key={`${rowIndex}-${column}`}
                        className="reportview-cell reportview-data-cell"
                      >
                        {data[column] && data[column][rowIndex] !== undefined
                          ? formatCellValue(data[column][rowIndex])
                          : "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderTwoKeyTable = (data) => {
    const rows = Object.keys(data);
    const allColumns = new Set();

    rows.forEach((row) => {
      if (data[row] && typeof data[row] === "object" && !Array.isArray(data[row])) {
        Object.keys(data[row]).forEach((column) => allColumns.add(column));
      }
    });

    const columns = Array.from(allColumns);

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table">
              <thead>
                <tr>
                  <th className="reportview-header reportview-row-header">
                    Category
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="reportview-header reportview-col-header"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row} className="reportview-row">
                    <td className="reportview-cell reportview-row-label">{row}</td>
                    {columns.map((column) => (
                      <td
                        key={`${row}-${column}`}
                        className="reportview-cell reportview-data-cell"
                      >
                        {data[row] && data[row][column] !== undefined
                          ? formatCellValue(data[row][column])
                          : "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderThreeKeyTable = (data) => {
    const level1Keys = Object.keys(data); // e.g., ["Monthly Performance trend...", "Performance slabs..."]

    return (
      <>
        <div className="reportview-container">
          {level1Keys.map((level1Key) => {
            const level2Data = data[level1Key];
            const level2Keys = Object.keys(level2Data); // e.g., ["Monthly Performance of the entire cohort", "Head count..."]
            const level3Keys = new Set();

            // Collect all level 3 keys (e.g., months)
            level2Keys.forEach((key2) => {
              if (level2Data[key2] && typeof level2Data[key2] === "object") {
                Object.keys(level2Data[key2]).forEach((key3) =>
                  level3Keys.add(key3)
                );
              }
            });

            const columns = Array.from(level3Keys).sort((a, b) => {
              // Sort months numerically (e.g., R1, R2, ..., CAP UPTO R12)
              if (a === "CAP UPTO R12") return 1;
              if (b === "CAP UPTO R12") return -1;
              const numA = parseInt(a.match(/R(\d+)/)?.[1] || 0);
              const numB = parseInt(b.match(/R(\d+)/)?.[1] || 0);
              return numA - numB;
            });

            return (
              <div key={level1Key} className="reportview-section">
                <div className="reportview-section-header">{level1Key}</div>
                <div className="rv-scroll-top">
                  <table className="reportview-table">
                    <thead>
                      <tr>
                        <th className="reportview-header reportview-row-header">
                          Metric
                        </th>
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="reportview-header reportview-col-header"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {level2Keys.map((row) => (
                        <tr key={row} className="reportview-row">
                          <td className="reportview-cell reportview-row-label">
                            {row}
                          </td>
                          {columns.map((col) => (
                            <td
                              key={`${row}-${col}`}
                              className="reportview-cell reportview-data-cell"
                            >
                              {level2Data[row] &&
                                level2Data[row][col] !== undefined
                                ? formatCellValue(level2Data[row][col])
                                : "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        <InsightsEditor />
      </>
    );
  };

  const renderFourKeyTable = (data) => {
    const level1Keys = Object.keys(data); // e.g., ["PBC"]
    const level2Keys = new Set(); // e.g., ["Head count of employees in various STANDARD performance value slabs", "Head count % of employees in various performance value slabs"]
    const level3Keys = new Set(); // e.g., ["Zero Performance Achievement", "Performance Achievement > 0 % and < 25 %", ...]
    const level4Keys = new Set(); // e.g., ["Residency Month R1", "Residency Month R2", ...]

    // Collect unique keys at each level
    level1Keys.forEach((key1) => {
      const level2 = data[key1];
      if (level2 && typeof level2 === "object") {
        Object.keys(level2).forEach((key2) => {
          level2Keys.add(key2);
          const level3 = level2[key2];
          if (level3 && typeof level3 === "object") {
            Object.keys(level3).forEach((key3) => {
              level3Keys.add(key3);
              const level4 = level3[key3];
              if (level4 && typeof level4 === "object") {
                Object.keys(level4).forEach((key4) => level4Keys.add(key4));
              }
            });
          }
        });
      }
    });

    const metrics = Array.from(level1Keys); // Level 1: Metrics
    const categories = Array.from(level2Keys); // Level 2: Categories
    const subcategories = Array.from(level3Keys); // Level 3: Subcategories (rows)
    const months = Array.from(level4Keys).sort((a, b) => {
      // Sort months assuming format "Residency Month RX"
      const numA = parseInt(a.match(/R(\d+)/)?.[1] || 0);
      const numB = parseInt(b.match(/R(\d+)/)?.[1] || 0);
      return numA - numB;
    }); // Level 4: Months (columns), sorted numerically

    return (
      <>
        <div className="reportview-container">
          <div className="rv-scroll-top">
            <table className="reportview-table">
              <thead>
                <tr>
                  {metrics.map((metric) => (
                    <th
                      key={metric}
                      className="reportview-header reportview-corner-header"
                      // colSpan={categories.length * months.length}
                      rowSpan="3"
                    >
                      {metric}
                    </th>
                  ))}
                  {/* <th
                  className="reportview-header reportview-corner-header"
                  rowSpan="3"
                  
                >
                  {metric}
                </th> */}
                  {/* {metrics.map((metric) => (
                  <th
                    key={metric}
                    className="reportview-header reportview-main-header"
                    colSpan={categories.length * months.length}
                  >
                    {metric}
                  </th>
                ))} */}
                </tr>
                <tr>
                  {metrics.map((metric) =>
                    categories.map((category) => (
                      <th
                        key={`${metric}-${category}`}
                        className="reportview-header reportview-sub-header"
                        colSpan={months.length}
                      >
                        {category}
                      </th>
                    ))
                  )}
                </tr>
                <tr>
                  {metrics.map((metric) =>
                    categories.map(() =>
                      months.map((month) => (
                        <th
                          key={`${metric}-${month}`}
                          className="reportview-header reportview-sub-sub-header"
                        >
                          {month}
                        </th>
                      ))
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {subcategories.map((subcategory) => (
                  <tr key={subcategory} className="reportview-row">
                    <td className="reportview-cell reportview-row-label">
                      {subcategory}
                    </td>
                    {metrics.map((metric) =>
                      categories.map((category) =>
                        months.map((month) => (
                          <td
                            key={`${metric}-${category}-${subcategory}-${month}`}
                            className="reportview-cell reportview-data-cell"
                          >
                            {data[metric] &&
                              data[metric][category] &&
                              data[metric][category][subcategory] &&
                              data[metric][category][subcategory][month] !==
                              undefined
                              ? formatCellValue(
                                data[metric][category][subcategory][month]
                              )
                              : "-"}
                          </td>
                        ))
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <InsightsEditor />
      </>
    );
  };

  const transformToRechartsFormat = (data) => {
    if (
      !data ||
      !data.data ||
      !data.data.stacked_data ||
      !data.data.trend_line_data
    ) {
      console.error(
        "Missing required data for recharts combination chart:",
        data
      );
      return [];
    }

    const keys = Object.keys(data.data.stacked_data);
    const trendLineData = data.data.trend_line_data;
    const isNested =
      Object.values(trendLineData).length > 0 &&
      typeof Object.values(trendLineData)[0] === "object";

    return keys.map((key) => {
      const entry = {
        month: key.replace("Residency Month ", ""),
        headcount: Math.round(data.data.stacked_data[key] * 100) / 100,
      };

      if (isNested) {
        Object.keys(trendLineData).forEach((metricName) => {
          const metricData = trendLineData[metricName];
          entry[metricName] =
            metricData[key] != null
              ? Math.round(metricData[key] * 100) / 100
              : null;
        });
      } else {
        entry.performance =
          trendLineData[key] != null
            ? Math.round(trendLineData[key] * 100) / 100
            : null;
      }

      return entry;
    });
  };
  const handleBack = () => {
    const resultsPerPage = location.state?.resultsPerPage || 10;
    navigate(`/architect/${inputID}/reports_list`, {
      state: {
        mart_id,
        projectId,
        inputID,
        clientID,
        roleId,
        projectName: location.state?.projectName,
        resultsPerPage,
      },
    });
  };

  const renderVisualContent = () => {
    if (visualLoading) {
      return <div className="reportview-loading">Loading visual data...</div>;
    }

    if (visualError) {
      return (
        <div className="reportview-error">
          No visual data available for this report
        </div>
      );
    }
    if (!visualData || visualData.length === 0) {
      return <div className="reportview-no-data">No visual data available</div>;
    }

    return (
      <>
        {/* <div className="visual-view-header">Graphs</div> */}
        <div>
          {visualData.map((item, index) => {
            console.log(
              `Rendering chart for question: ${item.question}, graph type: ${item.data?.["graph type"]}`,
            );
            let xAxisData = [];
            let seriesData = [];

            if (!item.data?.data) {
              console.error(`No data for question: ${item.question}`);
              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  <h2 className="report-view-question-subtitle">
                    {item.data?.sub_title}
                  </h2>
                  <div className="reportview-error">No data available</div>
                </div>
              );
            }

            const metrics = Object.keys(item.data.data);
            const firstMetricData = item.data.data[metrics[0]];

            if (item.data["graph type"] === "dotted chart") {
              const metrics = Object.keys(item.data.data || {});
              const seriesData = metrics.map((key) => ({
                id: key,
                label: key,
                average: item.data.data[key]?.average ?? null,
              }));

              // Prepare a single data point object like:
              // { label: "Average", metric1: 12.3, metric2: 10.5, ... }
              const transformedData = [
                seriesData.reduce(
                  (acc, metric) => {
                    acc[metric.id] = metric.average;
                    return acc;
                  },
                  { label: "Average" },
                ),
              ];

              console.log("Dotted chart - data:", transformedData);

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart
                      data={transformedData}
                      margin={{ top: 20, bottom: 50, left: 60, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={false}
                        axisLine={false}
                        label={{
                          value: item.data?.["x-axis"] || "Residency Month",
                          position: "insideBottom",
                          offset: -10,
                        }}
                      />
                      <YAxis
                        label={{
                          value:
                            item.data?.["y-axis"] || "Performance Achievement",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        allowDecimals={true}
                      />
                      <Tooltip
                        formatter={(value) =>
                          value == null ? "NaN" : formatChartNumber(value)
                        }
                      />
                      <Legend verticalAlign="top" height={36} />
                      {seriesData.map((metric, idx) => (
                        <Line
                          key={metric.id}
                          type="linear"
                          dataKey={metric.id}
                          stroke={
                            ["#3b82f6", "#ef4444", "#10b981", "#f59e0b"][
                            idx % 4
                            ]
                          }
                          strokeDasharray="5 5"
                          dot={({ cx, cy }) => (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="white"
                              stroke={idx === 0 ? "#ef4444" : "#3b82f6"}
                              strokeWidth={2}
                            />
                          )}
                          activeDot={{ r: 8 }}
                          isAnimationActive={true}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            } else if (item.data["graph type"] === "bar chart milestones") {
              const milestoneData = item.data.data;

              const chartData = Object.entries(milestoneData).map(
                ([key, value]) => ({
                  name: key,
                  value:
                    Math.round(
                      value[
                      "Average period to make the milestone in Residency months"
                      ] * 100,
                    ) / 100,
                }),
              );

              const MILESTONE_COLORS = ["#4ade80", "#22d3ee", "#a78bfa"];

              const MILESTONE_LABELS = {
                "Month of First Sale": "First Sale",
                "first month of 50% of target achievement": "50% of Target",
                "first month of 100% of target achievement": "100% of Target",
              };

              const MILESTONE_DESCRIPTIONS = {
                "Month of First Sale": "Average time to make the first sale",
                "first month of 50% of target achievement":
                  "Average time to achieve 50% of the target for the first time",
                "first month of 100% of target achievement":
                  "Average time to achieve 100% of the target for the first time",
              };

              const CylinderBar = (props) => {
                const { x, y, width, height, fill } = props;
                if (!height || height <= 0) return null;
                const rx = width / 2;
                const ry = rx * 0.35;
                return (
                  <g>
                    <rect
                      x={x}
                      y={y + ry}
                      width={width}
                      height={height - ry}
                      fill={fill}
                      opacity={0.85}
                    />
                    <ellipse
                      cx={x + width / 2}
                      cy={y + height}
                      rx={rx}
                      ry={ry}
                      fill={fill}
                      opacity={0.6}
                    />
                    <ellipse
                      cx={x + width / 2}
                      cy={y + ry}
                      rx={rx}
                      ry={ry}
                      fill="#ffffff"
                      opacity={0.25}
                    />
                    <ellipse
                      cx={x + width / 2}
                      cy={y + ry}
                      rx={rx}
                      ry={ry}
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth={1}
                      opacity={0.4}
                    />
                  </g>
                );
              };

              const ValueLabel = (props) => {
                const { x, y, width, value } = props;
                return (
                  <text
                    x={x + width / 2}
                    y={y - 8}
                    fill="#000000"
                    textAnchor="middle"
                    fontSize={16}
                    fontWeight="bold"
                  >
                    {value}
                  </text>
                );
              };

              const XTick = (props) => {
                const { x, y, payload, index } = props;
                const label = MILESTONE_LABELS[payload.value] || payload.value;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={0}
                      y={0}
                      dy={16}
                      textAnchor="middle"
                      fill="#000000"
                      fontSize={13}
                      fontWeight="bold"
                    >
                      {label}
                    </text>
                  </g>
                );
              };

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  <div
                    style={{
                      backgroundColor: "#f9fafb",
                      borderRadius: "12px",
                      padding: "30px 20px 20px",
                      display: "flex",
                      gap: "24px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: "32px",
                        minWidth: "220px",
                        paddingLeft: "10px",
                      }}
                    >
                      {chartData.map((entry, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor:
                                MILESTONE_COLORS[idx % MILESTONE_COLORS.length],
                              marginTop: "3px",
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <div
                              style={{
                                color: "#000000",
                                fontWeight: "bold",
                                fontSize: "13px",
                              }}
                            >
                              {MILESTONE_LABELS[entry.name] || entry.name}
                            </div>
                            <div
                              style={{
                                color: "#374151",
                                fontSize: "11px",
                                marginTop: "3px",
                                lineHeight: "1.4",
                              }}
                            >
                              {MILESTONE_DESCRIPTIONS[entry.name] || ""}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ flex: 1 }}>
                      <ResponsiveContainer width="100%" height={350}>
                        <RechartsBarChart
                          data={chartData}
                          margin={{ top: 40, right: 30, left: 10, bottom: 20 }}
                          barCategoryGap="30%"
                        >
                          <CartesianGrid vertical={false} stroke="#2d2d4e" />
                          <XAxis
                            dataKey="name"
                            tick={<XTick />}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis hide={true} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #d1d5db",
                              color: "#1f2937",
                            }}
                            formatter={(value) => [
                              `${formatChartNumber(value)} months`,
                              "Avg Period",
                            ]}
                          />
                          <Bar
                            dataKey="value"
                            shape={<CylinderBar />}
                            label={<ValueLabel />}
                            maxBarSize={80}
                          >
                            {chartData.map((entry, idx) => (
                              <Cell
                                key={`cell-${idx}`}
                                fill={
                                  MILESTONE_COLORS[
                                  idx % MILESTONE_COLORS.length
                                  ]
                                }
                              />
                            ))}
                          </Bar>
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              );
            } else if (item.data["graph type"] === "line chart trend") {
              const { xAxisData, seriesData } = transformToMuiLineChart(item.data);

              const transformedData = xAxisData.map((month, i) => {
                const entry = { month };
                seriesData.forEach((series) => {
                  entry[series.id] = series.data[i];
                });
                return entry;
              });

              const LINE_COLORS = [
                "#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6",
                "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
              ];

              const LineTrendTooltip = ({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const payloadMap = {};
                payload.forEach((p) => { payloadMap[p.dataKey] = p; });
                const ordered = seriesData.map((s) => payloadMap[s.id]).filter(Boolean);
                return (
                  <div style={{
                    background: "#fff", border: "1px solid #ccc", borderRadius: 6,
                    padding: "10px 14px", fontSize: 13, boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    maxWidth: 320,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
                    {ordered.map((p) => (
                      <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 16, color: p.stroke, marginBottom: 2 }}>
                        <span>{p.name}</span>
                        <span style={{ fontWeight: 500 }}>{roundToTwo(p.value)}</span>
                      </div>
                    ))}
                  </div>
                );
              };

              // How much headroom to add ABOVE the actual max value.
              // Shared helpers: Y_AXIS_TOP_GAP, getEvenYAxisBounds, VerticalZoomSlider

              const LineChartWithYZoom = () => {
                // Exact data bounds first (no rounding/padding here)
                const [dataMin, dataMax] = React.useMemo(() => {
                  const values = transformedData.flatMap((row) =>
                    seriesData.map((s) => row[s.id]).filter((v) => v !== undefined && v !== null)
                  );
                  if (values.length === 0) return [0, 1];
                  return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))];
                }, []);

                // Round upper bound up so tick intervals stay even (e.g. 128 → 140)
                const { yMin, yMax, ticks: yTicks } = React.useMemo(
                  () => getEvenYAxisBounds(dataMin, dataMax),
                  [dataMin, dataMax]
                );

                const [yRange, setYRange] = React.useState([yMin, yMax]);

                return (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <VerticalZoomSlider min={yMin} max={yMax} value={yRange} onChange={setYRange} height={260} />
                    <div style={{ flex: 1 }}>
                      <ResponsiveContainer width="100%" height={500}>
                        <LineChart
                          data={transformedData}
                          margin={{ top: 20, bottom: 75, left: 90, right: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            label={{
                              value: item.data?.["x-axis"] || "Residency Month",
                              position: "bottom",
                              offset: 68,
                            }}
                          />
                          <YAxis
                            domain={yRange}
                            ticks={
                              yRange[0] === yMin && yRange[1] === yMax
                                ? yTicks
                                : undefined
                            }
                            allowDataOverflow
                            allowDecimals={false}
                            label={{
                              value: item.data?.["y-axis"] || "Performance Achievement",
                              angle: -90,
                              position: "middle",
                              dx: -16,
                            }}
                          />
                          <Tooltip content={<LineTrendTooltip />} />
                          <Legend verticalAlign="bottom" height={36} />
                          {seriesData.map((series, idx) => (
                            <Line
                              key={series.id}
                              type="monotone"
                              dataKey={series.id}
                              stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                              strokeWidth={1.5}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                              label={{ position: "top", fill: "#333", fontSize: 10 }}
                            />
                          ))}

                          {/* X-axis zoom slider, same mechanism as Power BI's horizontal slider */}
                          <Brush dataKey="month" height={25} stroke="#3b82f6" travellerWidth={10} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              };

              return (
                <div key={index} className="graph-container" style={{ marginBottom: "30px" }}>
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  {Array.isArray(transformedData) && transformedData.length > 0 ? (
                    <LineChartWithYZoom />
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>No data available for chart</div>
                  )}
                </div>
              );
            } else if (item.data["graph type"] === "line chart trend secondary") {
              // ── Dual-axis line chart ──────────────────────────────────────
              // Series whose key contains "multiple" go on the LEFT axis;
              // all other series (attrition %) go on the RIGHT axis.
              const rawData = item.data?.data || {};
              const allSeriesKeys = Object.keys(rawData);

              // Sorted integer month indices (x-axis ticks)
              const monthIndices = Array.from(
                new Set(allSeriesKeys.flatMap((k) => Object.keys(rawData[k])))
              ).map(Number).sort((a, b) => a - b);

              // Chart data rows: { month: 1, seriesKeyA: val, seriesKeyB: val, ... }
              const dualChartData = monthIndices.map((m) => {
                const row = { month: `R${m}` };
                allSeriesKeys.forEach((k) => {
                  const v = rawData[k][String(m)];
                  row[k] = v === null || v === undefined ? null : Math.round(v * 100) / 100;
                });
                return row;
              });

              // Separate left (multiple) vs right (percentage) series
              const leftKeys = allSeriesKeys.filter((k) =>
                k.toLowerCase().includes("multiple")
              );
              const rightKeys = allSeriesKeys.filter((k) =>
                !k.toLowerCase().includes("multiple")
              );

              // Colour palette matching image: gold for multiple, blues for attrition
              const LEFT_COLORS = ["#c9a227", "#e8c04a", "#a07c1e"];
              const RIGHT_COLORS = ["#4472c4", "#1f3864", "#5b9bd5", "#2e75b6"];

              const hasLeft = leftKeys.length > 0;
              const hasRight = rightKeys.length > 0;

              // Custom tooltip
              const DualTooltip = ({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                return (
                  <div style={{
                    background: "#fff", border: "1px solid #ccc", borderRadius: 6,
                    padding: "10px 14px", fontSize: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.14)", maxWidth: 360,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
                    {payload.map((p) => (
                      <div key={p.dataKey} style={{
                        display: "flex", justifyContent: "space-between",
                        gap: 20, color: p.stroke, marginBottom: 3,
                      }}>
                        <span style={{ maxWidth: 240, whiteSpace: "normal" }}>{p.name}</span>
                        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {p.value == null ? "—" : (
                            rightKeys.includes(p.dataKey)
                              ? `${roundToTwo(p.value)}%`
                              : roundToTwo(p.value)
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              };

              // Short legend label (strip long parenthetical to keep legend readable)
              const shortLabel = (key) => {
                const idx = key.indexOf("-");
                return idx !== -1 ? key.slice(0, idx).trim() : key;
              };

              return (
                <div key={index} className="graph-container" style={{ marginBottom: "30px" }}>
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  <ResponsiveContainer width="100%" height={520}>
                    <ComposedChart
                      data={dualChartData}
                      margin={{ top: 20, right: 80, bottom: 80, left: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                      <XAxis
                        dataKey="month"
                        label={{
                          value: item.data?.["x-axis"] || "Residency Month",
                          position: "bottom",
                          offset: 55,
                          style: { fontSize: 13, fontWeight: 600 },
                        }}
                        tick={{ fontSize: 12 }}
                      />

                      {/* Left Y-axis — "secondary y-axis" label from API */}
                      {hasLeft && (
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          tickFormatter={(v) => roundToTwo(v)}
                          label={{
                            value: item.data?.["secondary y-axis"] || "Attrition Multiple",
                            angle: -90,
                            position: "insideLeft",
                            offset: -10,
                            style: { fontSize: 12, fontWeight: 600 },
                          }}
                          tick={{ fontSize: 11 }}
                          allowDecimals={true}
                        />
                      )}

                      {/* Right Y-axis — "y-axis" label from API */}
                      {hasRight && (
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tickFormatter={(v) => `${roundToTwo(v)}%`}
                          label={{
                            value: item.data?.["y-axis"] || "Attrition %",
                            angle: 90,
                            position: "insideRight",
                            offset: 20,
                            style: { fontSize: 12, fontWeight: 600 },
                          }}
                          tick={{ fontSize: 11 }}
                          allowDecimals={true}
                        />
                      )}

                      <Tooltip content={<DualTooltip />} />

                      <Legend
                        verticalAlign="top"
                        height={50}
                        formatter={(value) => (
                          <span style={{ fontSize: 12 }}>{shortLabel(value)}</span>
                        )}
                      />

                      {/* Left-axis lines (attrition multiple) */}
                      {leftKeys.map((key, idx) => (
                        <Line
                          key={key}
                          yAxisId="left"
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke={LEFT_COLORS[idx % LEFT_COLORS.length]}
                          strokeWidth={2.5}
                          dot={{ r: 4, strokeWidth: 2 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                          label={({ x, y, value }) =>
                            value != null ? (
                              <text x={x} y={y - 8} textAnchor="middle" fontSize={11}
                                fill={LEFT_COLORS[idx % LEFT_COLORS.length]} fontWeight={600}>
                                {roundToTwo(value)}
                              </text>
                            ) : null
                          }
                        />
                      ))}

                      {/* Right-axis lines (attrition %) */}
                      {rightKeys.map((key, idx) => (
                        <Line
                          key={key}
                          yAxisId="right"
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke={RIGHT_COLORS[idx % RIGHT_COLORS.length]}
                          strokeWidth={2.5}
                          dot={{ r: 4, strokeWidth: 2 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                          label={({ x, y, value }) =>
                            value != null ? (
                              <text x={x} y={y - 8} textAnchor="middle" fontSize={11}
                                fill={RIGHT_COLORS[idx % RIGHT_COLORS.length]} fontWeight={600}>
                                {`${roundToTwo(value)}%`}
                              </text>
                            ) : null
                          }
                        />
                      ))}

                      <Brush dataKey="month" height={22} stroke="#4472c4" travellerWidth={8} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            } else if (item.data["graph type"] === "Stacked Column Chart") {
              const { xAxisData, seriesData } = transformToMuiLineChart(
                item.data,
              );

              console.log(
                "Stacked bar chart - xAxisData:",
                xAxisData,
                "seriesData:",
                seriesData,
              );

              const modifiedSeries = seriesData.map((series, idx) => ({
                ...series,
                stack: "total",
                stackOrder: idx === 0 ? stackOrder : undefined,
              }));

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  {Array.isArray(xAxisData) &&
                    Array.isArray(seriesData) &&
                    seriesData.length > 0 ? (
                    <BarChart
                      xAxis={[
                        {
                          data: xAxisData,
                          label: item.data?.["x-axis"] || "X-Axis",
                          scaleType: "band",
                          tickLabelStyle: {
                            angle: -90,
                            dominantBaseline: "auto",
                            textAnchor: "end",
                          },
                          height: 180,
                        },
                      ]}
                      yAxis={[
                        {
                          label:
                            item.data?.["y-axis"] || "Performance Achievement",
                          min: 0,
                        },
                      ]}
                      series={modifiedSeries}
                      height={500}
                      margin={{ top: 20, bottom: 40, left: 30, right: 20 }}
                      colors={[
                        "#D64550",
                        "#118DFF",
                        "#12239E",
                        "#E66C37",
                        "#6B007B",
                        "#E044A7",
                        "#744EC2",
                        "#D6C044",
                        "#808080",
                        "#06A524",
                      ]}
                      grid={{ vertical: false, horizontal: false }}
                      slotProps={{
                        legend: {
                          direction: "row",
                          position: {
                            vertical: "bottom",
                            horizontal: "middle",
                          },
                          padding: 0,
                        },
                      }}
                    />
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}
                </div>
              );
            } else if (item.data["graph type"] === "GENDER VISUAL") {
              const genderData = item.data.data;
              const maleCount = genderData?.count?.male || 0;
              const femaleCount = genderData?.count?.female || 0;
              const malePercentage = genderData?.percentage?.male || 0;
              const femalePercentage = genderData?.percentage?.female || 0;

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "40px 20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        border: "3px solid #000",
                        borderRadius: "8px",
                        overflow: "hidden",
                        maxWidth: "500px",
                        width: "100%",
                      }}
                    >
                      {/* Male Section */}
                      <div
                        style={{
                          flex: 1,
                          padding: "30px 20px",
                          textAlign: "center",
                          borderRight: "3px solid #000",
                          backgroundColor: "#fff",
                        }}
                      >
                        <div style={{ marginBottom: "15px" }}>
                          <svg
                            width="80"
                            height="80"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#6b7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ margin: "0 auto" }}
                          >
                            <circle cx="10" cy="14" r="5" />
                            <line x1="14" y1="10" x2="21" y2="3" />
                            <line x1="16" y1="3" x2="21" y2="3" />
                            <line x1="21" y1="3" x2="21" y2="8" />
                          </svg>
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#000",
                            marginBottom: "8px",
                          }}
                        >
                          Male
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#000",
                            marginBottom: "5px",
                          }}
                        >
                          {maleCount.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#3b82f6",
                            fontWeight: "600",
                          }}
                        >
                          {roundToTwo(malePercentage).toFixed(2)}%
                        </div>
                      </div>

                      {/* Female Section */}
                      <div
                        style={{
                          flex: 1,
                          padding: "30px 20px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                        }}
                      >
                        <div style={{ marginBottom: "15px" }}>
                          <svg
                            width="80"
                            height="80"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#6b7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ margin: "0 auto" }}
                          >
                            <circle cx="12" cy="9" r="5" />
                            <line x1="12" y1="14" x2="12" y2="21" />
                            <line x1="9" y1="18" x2="15" y2="18" />
                          </svg>
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#000",
                            marginBottom: "8px",
                          }}
                        >
                          Female
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#000",
                            marginBottom: "5px",
                          }}
                        >
                          {femaleCount.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#ef4444",
                            fontWeight: "600",
                          }}
                        >
                          {roundToTwo(femalePercentage).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (item.data["graph type"] === "bar chart") {
              const barData = item.data.data;

              // Transform data for recharts BarChart
              const chartData = Object.keys(barData.count || {}).map((key) => ({
                ageGroup: key,
                count: barData.count[key],
                percentage: barData.percentage[key],
              }));

              // Color palette for bars
              const COLORS = [
                "#118DFF",
                "#12239E",
                "#D64550",
                "#E66C37",
                "#6B007B",
                "#E044A7",
                "#744EC2",
                "#D6C044",
                "#808080",
              ];

              // Get color based on index
              const getBarColor = (index) => COLORS[index % COLORS.length];

              const maxCount = Math.max(...Object.values(barData.count || {}));

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={500}>
                      <RechartsBarChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="ageGroup"
                          angle={0}
                          textAnchor="middle"
                          height={60}
                          tick={{ fontSize: 12 }}
                          label={{
                            value: item.data?.["x-axis"] || "Age Bucket",
                            position: "insideBottom",
                            offset: -10,
                            style: { fontSize: 14, fontWeight: 600 },
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          label={{
                            value: item.data?.["y-axis"] || "Employee Count",
                            angle: -90,
                            position: "middle",
                            style: { fontSize: 14, fontWeight: 600 },
                          }}
                          domain={[0, maxCount + maxCount * 0.1]}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [
                            `Count: ${formatChartNumber(value)} (${roundToTwo(props.payload.percentage).toFixed(2)}%)`,
                            "",
                          ]}
                          labelFormatter={(label) => `Age Group: ${label}`}
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            padding: "10px",
                          }}
                        />
                        <Bar
                          dataKey="count"
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: "top",
                            formatter: (value) =>
                              value > 0 ? formatChartNumber(value) : "",
                            style: {
                              fontSize: 11,
                              fontWeight: 600,
                              fill: "#333",
                            },
                          }}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={getBarColor(index)}
                            />
                          ))}
                        </Bar>
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}

                  {/* Summary statistics */}
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "15px",
                      backgroundColor: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      display: "flex",
                      gap: "30px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Total Employees
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {Object.values(barData.count || {})
                          .reduce((sum, val) => sum + val, 0)
                          .toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Most Common Age Group
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {Object.entries(barData.count || {}).reduce(
                          (max, [key, val]) =>
                            val > (barData.count[max] || 0) ? key : max,
                          Object.keys(barData.count || {})[0],
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Highest Count
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {maxCount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (item.data["graph type"] === "Scatter Plot Individual") {
              const { vertical = [], horizontal = [] } =
                item.data.gridlines || {};

              const individualPoints = Object.entries(item.data.data || {})
                .map(([id, coords]) => {
                  const rawX = Array.isArray(coords) ? coords[0] : coords?.x;
                  const rawY = Array.isArray(coords) ? coords[1] : coords?.y;
                  const x = parseChartNumber(rawX);
                  const y = parseChartNumber(rawY);
                  if (x === null || y === null) return null;
                  return { id, x: roundToTwo(x), y: roundToTwo(y) };
                })
                .filter(Boolean);

              const hasData = individualPoints.length > 0;

              const xTicks = [...new Set(horizontal.map(parseChartNumber))]
                .filter((v) => v !== null)
                .sort((a, b) => a - b);
              const yTicks = [...new Set(vertical.map(parseChartNumber))]
                .filter((v) => v !== null)
                .sort((a, b) => a - b);

              const allX = individualPoints.map((p) => p.x);
              const allY = individualPoints.map((p) => p.y);
              const xMax = allX.length
                ? Math.max(...allX, ...(xTicks.length ? xTicks : []))
                : 100;
              const yMax = allY.length
                ? Math.max(...allY, ...(yTicks.length ? yTicks : []))
                : 100;

              // If the API supplies explicit gridlines: extend by one step for a
              // consistent final label.  Otherwise fall back to computeNiceTicks
              // so Recharts never invents its own "nice ceiling" (e.g. 108).
              const xStep = xTicks.length >= 2
                ? xTicks[xTicks.length - 1] - xTicks[xTicks.length - 2] : 0;
              const { ticks: xTicksFinal, domainMax: xDomainMax } = xStep > 0
                ? {
                  ticks: [...xTicks, xTicks[xTicks.length - 1] + xStep],
                  domainMax: xTicks[xTicks.length - 1] + xStep
                }
                : computeNiceTicks(xMax);

              const yStep = yTicks.length >= 2
                ? yTicks[yTicks.length - 1] - yTicks[yTicks.length - 2] : 0;
              const { ticks: yTicksFinal, domainMax: yDomainMax } = yStep > 0
                ? {
                  ticks: [...yTicks, yTicks[yTicks.length - 1] + yStep],
                  domainMax: yTicks[yTicks.length - 1] + yStep
                }
                : computeNiceTicks(yMax);

              const IndividualDot = (props) => {
                const { cx, cy } = props;
                const [hovered, setHovered] = React.useState(false);
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={hovered ? 9 : 6}
                    stroke={hovered ? "#000000" : "#1976d2"}
                    strokeWidth={hovered ? 2 : 0}
                    fill="#1976d2"
                    style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                  />
                );
              };

              const IndividualTooltip = ({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null;
                const point = payload[0]?.payload;
                if (!point) return null;
                return (
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {point.id}
                    </div>
                    <div>
                      {item.data?.["x-axis"] || "X"}: {formatChartNumber(point.x)}
                    </div>
                    <div>
                      {item.data?.["y-axis"] || "Y"}: {formatChartNumber(point.y)}
                    </div>
                  </div>
                );
              };

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  {item.data?.sub_title && (
                    <h2 className="report-view-question-subtitle">
                      {item.data.sub_title}
                    </h2>
                  )}
                  {hasData ? (
                    <ResponsiveContainer width="100%" height={500}>
                      <RechartsScatterChart
                        margin={{ left: 70, bottom: 60, right: 30, top: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          dataKey="x"
                          domain={[0, xDomainMax]}
                          ticks={xTicksFinal.length ? xTicksFinal : undefined}
                          label={{
                            value: item.data?.["x-axis"] || "",
                            position: "insideBottom",
                            offset: -15,
                          }}
                          tickFormatter={formatChartNumber}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          domain={[0, yDomainMax]}
                          ticks={yTicksFinal.length ? yTicksFinal : undefined}
                          label={{
                            value: item.data?.["y-axis"] || "",
                            angle: -90,
                            position: "insideMiddle",
                            dx: -10,
                          }}
                          tickFormatter={formatChartNumber}
                        />
                        <Tooltip content={<IndividualTooltip />} cursor={false} />
                        <Scatter
                          name="Supervisors"
                          data={individualPoints}
                          fill="#1976d2"
                          shape={<IndividualDot />}
                        />
                      </RechartsScatterChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}
                </div>
              );
            } else if (item.data["graph type"] === "Scatter Plot") {
              const { vertical = [], horizontal = [] } =
                item.data.gridlines || {};

              const seriesKeys = Object.keys(item.data.data || {});

              const SERIES_COLORS = {
                Active: "#1976d2",
                default: "#d32f2f",
              };

              const hasData = seriesKeys.some(
                (key) => (item.data.data[key] || []).length > 0
              );

              // FIXED: swapped horizontal/vertical assignment
              const xTicks = [...new Set(horizontal.map(parseChartNumber))]
                .filter((v) => v !== null)
                .sort((a, b) => a - b);
              const yTicks = [...new Set(vertical.map(parseChartNumber))]
                .filter((v) => v !== null)
                .sort((a, b) => a - b);

              const allPoints = seriesKeys.flatMap((key) =>
                (item.data.data[key] || [])
                  .map((point) => {
                    const rawX = Array.isArray(point) ? point[0] : point?.x;
                    const rawY = Array.isArray(point) ? point[1] : point?.y;
                    const x = parseChartNumber(rawX);
                    const y = parseChartNumber(rawY);
                    if (x === null || y === null) return null;
                    return { x: roundToTwo(x), y: roundToTwo(y) };
                  })
                  .filter(Boolean)
              );

              const allX = allPoints.map((p) => p.x);
              const allY = allPoints.map((p) => p.y);
              const xMax = allX.length ? Math.max(...allX, ...(xTicks.length ? xTicks : [])) : 30;
              const yMax = allY.length ? Math.max(...allY, ...(yTicks.length ? yTicks : [])) : 100;

              const xStep2 = xTicks.length >= 2
                ? xTicks[xTicks.length - 1] - xTicks[xTicks.length - 2] : 0;
              const { ticks: xTicksFinal, domainMax: xDomainMax } = xStep2 > 0
                ? {
                  ticks: [...xTicks, xTicks[xTicks.length - 1] + xStep2],
                  domainMax: xTicks[xTicks.length - 1] + xStep2
                }
                : computeNiceTicks(xMax);

              const yStep2 = yTicks.length >= 2
                ? yTicks[yTicks.length - 1] - yTicks[yTicks.length - 2] : 0;
              const { ticks: yTicksFinal, domainMax: yDomainMax } = yStep2 > 0
                ? {
                  ticks: [...yTicks, yTicks[yTicks.length - 1] + yStep2],
                  domainMax: yTicks[yTicks.length - 1] + yStep2
                }
                : computeNiceTicks(yMax);

              const CustomDot = (props) => {
                const { cx, cy, payload } = props;
                const color = SERIES_COLORS[payload.series] || SERIES_COLORS.default;
                const [hovered, setHovered] = React.useState(false);
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={hovered ? 9 : 6}
                    stroke={hovered ? "#000000" : color}
                    strokeWidth={hovered ? 2 : 0}
                    fill={color}
                    style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                  />
                );
              };

              const CustomTooltip = ({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null;
                const point = payload[0]?.payload;
                if (!point) return null;
                return (
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{point.series}</div>
                    <div>{item.data?.["x-axis"] || "X"}: {formatChartNumber(point.x)}</div>
                    <div>{item.data?.["y-axis"] || "Y"}: {formatChartNumber(point.y)}</div>
                  </div>
                );
              };

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  {hasData ? (
                    <ResponsiveContainer width="100%" height={500}>
                      <RechartsScatterChart
                        margin={{ left: 70, bottom: 60, right: 30, top: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          dataKey="x"
                          domain={[0, xDomainMax]}
                          ticks={xTicksFinal.length ? xTicksFinal : undefined}
                          label={{
                            value: item.data?.["x-axis"] || "",
                            position: "insideBottom",
                            offset: -15,
                          }}
                          tickFormatter={formatChartNumber}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          domain={[0, yDomainMax]}
                          ticks={yTicksFinal.length ? yTicksFinal : undefined}
                          label={{
                            value: item.data?.["y-axis"] || "",
                            angle: -90,
                            position: "insideLeft",
                            dx: -10,
                          }}
                          tickFormatter={formatChartNumber}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={false}
                        />
                        <Legend verticalAlign="top" />
                        {seriesKeys.map((key) => {
                          const seriesPoints = (item.data.data[key] || [])
                            .map((point) => {
                              const rawX = Array.isArray(point) ? point[0] : point?.x;
                              const rawY = Array.isArray(point) ? point[1] : point?.y;
                              const x = parseChartNumber(rawX);
                              const y = parseChartNumber(rawY);
                              if (x === null || y === null) return null;
                              return { x: roundToTwo(x), y: roundToTwo(y), series: key };
                            })
                            .filter(Boolean);

                          return (
                            <Scatter
                              key={key}
                              name={key}
                              data={seriesPoints}
                              fill={SERIES_COLORS[key] || SERIES_COLORS.default}
                              shape={<CustomDot />}
                            />
                          );
                        })}
                      </RechartsScatterChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}
                </div>
              );
            } else if (item.data["graph type"] === "STATE VISUAL") {
              const stateData = item.data.data;

              const sortedStates = Object.keys(stateData.count || {})
                .map((state) => ({
                  name: state,
                  count: stateData.count[state],
                  percentage: stateData.percentage[state],
                }))
                .sort((a, b) => b.count - a.count);

              const totalCount = Object.values(stateData.count || {}).reduce(
                (sum, val) => sum + val,
                0,
              );

              const option = {
                series: [
                  {
                    type: "map",
                    map: "india",
                    roam: false,

                    // FORCE labels to show for ALL states
                    label: {
                      show: true,
                      fontSize: 10,
                      color: "#000",
                      fontWeight: "600",
                      formatter: (params) => {
                        // Show the state name from GeoJSON
                        return params.name;
                      },
                    },

                    itemStyle: {
                      borderColor: "#333",
                      borderWidth: 2,
                      areaColor: "#93c5fd", // Solid blue
                    },

                    emphasis: {
                      label: {
                        show: true,
                        fontSize: 12,
                        color: "#fff",
                        fontWeight: "bold",
                      },
                      itemStyle: {
                        areaColor: "#3b82f6", // Darker blue on hover
                        borderColor: "#000",
                        borderWidth: 3,
                      },
                    },
                  },
                ],
              };

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  <div
                    style={{
                      display: "flex",
                      gap: "40px",
                      padding: "20px",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* INDIA MAP */}
                    <div
                      style={{
                        flex: "0 0 500px",
                        padding: "20px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <ReactECharts
                        option={option}
                        style={{ height: "600px", width: "100%" }}
                        opts={{ renderer: "canvas" }}
                      />

                      <div
                        style={{
                          marginTop: "10px",
                          textAlign: "center",
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: "600",
                        }}
                      >
                        Total Employees: {totalCount.toLocaleString()}
                      </div>
                    </div>

                    {/* STATE TABLE */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          maxHeight: "600px",
                          overflowY: "auto",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "13px",
                          }}
                        >
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#f3f4f6",
                              zIndex: 1,
                            }}
                          >
                            <tr>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e5e7eb",
                                }}
                              >
                                State
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #e5e7eb",
                                }}
                              >
                                Count
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #e5e7eb",
                                }}
                              >
                                Percentage
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  borderBottom: "2px solid #e5e7eb",
                                }}
                              >
                                Distribution
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortedStates.map((state, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  backgroundColor:
                                    idx % 2 === 0 ? "#fff" : "#f9fafb",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "10px",
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  {state.name}
                                </td>

                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    fontWeight: "600",
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  {state.count.toLocaleString()}
                                </td>

                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  {roundToTwo(state.percentage).toFixed(2)}%
                                </td>

                                <td
                                  style={{
                                    padding: "10px",
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "100%",
                                      backgroundColor: "#e5e7eb",
                                      borderRadius: "4px",
                                      height: "8px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${Math.min(state.percentage * 4, 100)}%`,
                                        backgroundColor: "#744EC2",
                                        height: "100%",
                                      }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (item.data["graph type"] === "doughnut") {
              const doughnutData = item.data.data;

              // Transform data for recharts PieChart
              const chartData = Object.keys(doughnutData.count || {}).map(
                (key, index) => ({
                  name:
                    key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/_/g, " "),
                  value: doughnutData.count[key],
                  percentage: doughnutData.percentage[key],
                }),
              );

              // Color palette
              const COLORS = [
                "#118DFF",
                "#12239E",
                "#D64550",
                "#E66C37",
                "#6B007B",
                "#E044A7",
                "#744EC2",
                "#D6C044",
                "#808080",
                "#06A524",
                "#FF6B9D",
                "#C9CBCF",
                "#4ECDC4",
                "#FF6F61",
                "#6A0572",
              ];

              const totalCount = Object.values(doughnutData.count || {}).reduce(
                (sum, val) => sum + val,
                0,
              );

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  {chartData && chartData.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "40px",
                        padding: "20px",
                      }}
                    >
                      <ResponsiveContainer width="50%" height={400}>
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={140}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percentage }) =>
                              `${roundToTwo(percentage).toFixed(2)}%`
                            }
                            labelLine={true}
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => [
                              `${formatChartNumber(value)} (${roundToTwo(props.payload.percentage).toFixed(2)}%)`,
                              props.payload.name,
                            ]}
                          />
                          {/* Center label */}
                          <text
                            x="50%"
                            y="48%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              fill: "#333",
                            }}
                          >
                            Total
                          </text>
                          <text
                            x="50%"
                            y="52%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                              fill: "#000",
                            }}
                          >
                            {totalCount.toLocaleString()}
                          </text>
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Legend */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          maxWidth: "300px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            marginBottom: "8px",
                            color: "#000",
                          }}
                        >
                          Designation
                        </div>
                        {chartData.map((entry, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontSize: "13px",
                            }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: COLORS[index % COLORS.length],
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ color: "#333" }}>{entry.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}
                </div>
              );
            } else if (item.data["graph type"] === "bar chart horizontal") {
              const barData = item.data.data;

              // Transform data for recharts BarChart
              const chartData = Object.keys(barData.count || {}).map((key) => ({
                experience: key,
                count: barData.count[key],
                percentage: barData.percentage[key],
              }));

              // Color palette for bars
              const COLORS = [
                "#118DFF",
                "#12239E",
                "#D64550",
                "#E66C37",
                "#6B007B",
                "#E044A7",
                "#744EC2",
                "#D6C044",
                "#808080",
              ];

              // Get color based on index
              const getBarColor = (index) => COLORS[index % COLORS.length];

              const maxCount = Math.max(...Object.values(barData.count || {}));

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>

                  {chartData && chartData.length > 0 ? (
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(400, chartData.length * 40)}
                    >
                      <RechartsBarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 20, right: 80, left: 150, bottom: 20 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 12 }}
                          label={{
                            value: item.data?.["x-axis"] || "Employee Count",
                            position: "insideBottom",
                            offset: -10,
                            style: { fontSize: 14, fontWeight: 600 },
                          }}
                          domain={[0, maxCount + maxCount * 0.1]}
                        />
                        <YAxis
                          type="category"
                          dataKey="experience"
                          tick={{ fontSize: 11 }}
                          width={140}
                          label={{
                            value: item.data?.["y-axis"] || "Experience Range",
                            angle: -90,
                            position: "insideLeft",
                            style: { fontSize: 14, fontWeight: 600 },
                          }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [
                            `Count: ${formatChartNumber(value)} (${roundToTwo(props.payload.percentage).toFixed(2)}%)`,
                            "",
                          ]}
                          labelFormatter={(label) => `Experience: ${label}`}
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            padding: "10px",
                          }}
                        />
                        <Bar
                          dataKey="count"
                          radius={[0, 4, 4, 0]}
                          label={{
                            position: "right",
                            formatter: (value) =>
                              value > 0 ? formatChartNumber(value) : "",
                            style: {
                              fontSize: 11,
                              fontWeight: 600,
                              fill: "#333",
                            },
                          }}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={getBarColor(index)}
                            />
                          ))}
                        </Bar>
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}

                  {/* Summary statistics */}
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "15px",
                      backgroundColor: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      display: "flex",
                      gap: "30px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Total Employees
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {Object.values(barData.count || {})
                          .reduce((sum, val) => sum + val, 0)
                          .toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Experience Ranges with Data
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {chartData.length}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Highest Count
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {maxCount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (
              item.data["graph type"] === "Line and Stacked Column Chart"
            ) {
              const cd = formatCellValue(item.data);
              const chartData = transformToRechartsFormat(cd);

              // Get trend line metric names from data
              const trendLineMetrics = Object.keys(
                item.data?.data?.trend_line_data || {},
              ).filter(
                (key) =>
                  typeof item.data.data.trend_line_data[key] === "object",
              );

              // Color palette for multiple lines (high contrast colors)
              const lineColors = ["#D64550", "#FF9F1C", "#7C3AED", "#00D9FF"];

              const lineKeys =
                trendLineMetrics.length > 0
                  ? trendLineMetrics
                  : ["performance"];

              const ComposedChartWithYZoom = () => {
                // Left axis (bars / headcount)
                const [leftMin, leftMax] = React.useMemo(() => {
                  const values = chartData
                    .map((row) => row.headcount)
                    .filter((v) => v !== undefined && v !== null);
                  if (values.length === 0) return [0, 1];
                  return [Math.floor(Math.min(...values, 0)), Math.ceil(Math.max(...values))];
                }, []);

                const {
                  yMin: leftYMin,
                  yMax: leftYMax,
                  ticks: leftTicks,
                } = React.useMemo(
                  () => getEvenYAxisBounds(leftMin, leftMax),
                  [leftMin, leftMax]
                );

                // Right axis (trend lines / performance %)
                const [rightMin, rightMax] = React.useMemo(() => {
                  const values = chartData.flatMap((row) =>
                    lineKeys
                      .map((k) => row[k])
                      .filter((v) => v !== undefined && v !== null)
                  );
                  if (values.length === 0) return [0, 1];
                  return [Math.floor(Math.min(...values, 0)), Math.ceil(Math.max(...values))];
                }, []);

                const {
                  yMin: rightYMin,
                  yMax: rightYMax,
                  ticks: rightTicks,
                } = React.useMemo(
                  () => getEvenYAxisBounds(rightMin, rightMax),
                  [rightMin, rightMax]
                );

                const [yRange, setYRange] = React.useState([leftYMin, leftYMax]);

                return (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <VerticalZoomSlider
                      min={leftYMin}
                      max={leftYMax}
                      value={yRange}
                      onChange={setYRange}
                      height={260}
                    />
                    <div style={{ flex: 1 }}>
                      <ResponsiveContainer width="100%" height={500}>
                        <ComposedChart
                          data={chartData}
                          margin={{ top: 20, right: 40, left: 20, bottom: 75 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            label={{
                              value: item.data?.["x-axis"] || "Residency Month",
                              position: "bottom",
                              offset: 50,
                            }}
                          />
                          <YAxis
                            yAxisId="left"
                            orientation="left"
                            domain={yRange}
                            ticks={
                              yRange[0] === leftYMin && yRange[1] === leftYMax
                                ? leftTicks
                                : undefined
                            }
                            allowDataOverflow
                            allowDecimals={false}
                            label={{
                              value: item.data?.["y-axis"] || "Head Count",
                              angle: -90,
                              position: "insideLeft",
                              dy: 80,
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[rightYMin, rightYMax]}
                            ticks={rightTicks}
                            allowDataOverflow
                            allowDecimals={false}
                            label={{
                              value: "Performance %",
                              angle: 90,
                              position: "insideRight",
                            }}
                          />
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} />
                          <Bar
                            yAxisId="left"
                            dataKey="headcount"
                            fill="#118DFF"
                            name="Head Count of Employees"
                            label={{
                              position: "top",
                              fill: "#333",
                              fontSize: 12,
                            }}
                          />
                          {trendLineMetrics.length > 0 ? (
                            trendLineMetrics.map((metricName, idx) => (
                              <Line
                                key={metricName}
                                yAxisId="right"
                                type="monotone"
                                dataKey={metricName}
                                stroke={lineColors[idx % lineColors.length]}
                                strokeWidth={3}
                                name={metricName}
                                dot={{
                                  fill: lineColors[idx % lineColors.length],
                                  strokeWidth: 2,
                                  r: 4,
                                }}
                                label={{
                                  position: "top",
                                  fill: "#333",
                                  fontSize: 12,
                                }}
                              />
                            ))
                          ) : (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="performance"
                              stroke="#D64550"
                              strokeWidth={3}
                              name="Cumulative Avg Performance"
                              dot={{ fill: "#D64550", strokeWidth: 2, r: 4 }}
                              label={{
                                position: "top",
                                fill: "#333",
                                fontSize: 12,
                              }}
                            />
                          )}
                          <Brush dataKey="month" height={25} stroke="#3b82f6" travellerWidth={10} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              };

              return (
                <div
                  key={index}
                  className="graph-container"
                  style={{ marginBottom: "30px" }}
                >
                  <h3 className="report-view-question-title">
                    {item.data?.title || item.question}
                  </h3>
                  {chartData && chartData.length > 0 ? (
                    <ComposedChartWithYZoom />
                  ) : (
                    <div style={{ padding: "1rem", color: "gray" }}>
                      No data available for chart
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div
                key={index}
                className="graph-container"
                style={{ marginBottom: "30px" }}
              >
                <h3 className="report-view-question-title">
                  {item.data?.title || item.question}
                </h3>
                <div className="reportview-error">
                  Invalid graph type: {item.data?.["graph type"]}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <div className="reportview-loading">Loading report data...</div>;
    }

    if (activeTab === "table") {
      if (error) {
        console.error("Error loading report data:", error);
        return <div className="reportview-error">Error: {error}</div>;
      }
      return renderTable();
    }
    if (activeTab === "visual") {
      return renderVisualContent();
    }
    return null;
  };

  return (
    <div className="reportview-wrapper">
      <div className="reportview-title">
        <div className="reportview-title-c">
          <button className="reportview-back-arrow" onClick={handleBack}>
            <span className="back-arrow">
              <ChevronLeft style={{ marginTop: "5px" }} />
            </span>
            <h5 className="reportview-title-text">
              {reportTitle || "Loading title..."}
            </h5>
          </button>

          <h5 className="reportview-subtitle">
            {reportSubtitle || "Loading subtitle..."}
          </h5>
        </div>
      </div>

      <div className="tab-container">
        <button
          className={`tab-button ${activeTab === "table" ? "active" : ""}`}
          onClick={() => setActiveTab("table")}
        >
          Table View
        </button>
        <button
          className={`tab-button ${activeTab === "visual" ? "active" : ""}`}
          onClick={() => setActiveTab("visual")}
        >
          Visual View
        </button>
      </div>

      {isKpiDependent && kpiList.length > 0 && (
        <div
          className="kpi-dropdown-container"
          style={{ padding: "12px 16px" }}
        >
          <label
            htmlFor="kpi-select"
            style={{ marginRight: "10px", fontWeight: 500 }}
          >
            Select KPI:
          </label>
          <select
            id="kpi-select"
            value={selectedKpiId}
            onChange={(e) => setSelectedKpiId(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            {kpiList.map((kpi) => (
              <option key={kpi.id} value={kpi.id}>
                {kpi.kpi_name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="tab-content">{renderContent()}</div>
    </div>
  );
};

export default ReportView;
